import axios from "axios";
import moment from "moment";
//import utils from "@/common/utils.js";

export const state = () => ({
	initialised: false,
	loading: false,
	docsList: [],
	userName: "",
	hierarchyTypes: [],
	classifierTypes: [],
	allDocStatuses: [],
	docStatusSummary: [],
	docStatusSummaryFiltered: [],
	docTypeSummary: [],
	reviewGroups: [],
	reviewGroupsSummary: [],
	questionnaires: [],
	peopleSummary: [],
	createExtraFields: [],
	templateLinks: [],
	copyFromActions: [],
	docScoreCategories: [
		{ text: "Low", min: 0, max: 40, color: "red" },
		{ text: "Medium", min: 40, max: 75, color: "amber" },
		{ text: "High", min: 75, max: 100, color: "green" }
	],
	useTranslation: false,
	useIndex: false,
	listUpdated: null,
	fromTable: false,
	docListColumns: null
});

let refreshTimeout;
const setRefreshTimeout = (state) => {
	if (refreshTimeout) {
		clearTimeout(refreshTimeout);
	}
	refreshTimeout = setTimeout(() => {
		state.listUpdated = new Date();
	}, 5000);
}

export const mutations = {
	initialised(state) {
		state.initialised = true;
		state.loading = true;
	},
	clear(state) {
		state.docsList.splice(0);
		state.hierarchyTypes.splice(0);
		state.classifierTypes.splice(0);
		state.allDocStatuses.splice(0);
		state.docStatusSummary.splice(0);
		state.docStatusSummaryFiltered.splice(0);
		state.docTypeSummary.splice(0);
		state.reviewGroups.splice(0);
		state.reviewGroupsSummary.splice(0);
		state.questionnaires.splice(0);
		state.peopleSummary.splice(0);
		state.createExtraFields.splice(0);
		state.templateLinks.splice(0);
		state.copyFromActions.splice(0);
		state.listUpdated = null;
		state.fromTable = false;
	},
	unload(state) {
		state.initialised = false;
		state.loading = false;
		mutations.clear(state);
	},
	replace(state, data) {
		mutations.clear(state);

		state.userName = data.userName;
		state.useTranslation = data.useTranslation;
		state.useIndex = data.useIndex;
		state.docsList.push(...data.docsList);
		state.hierarchyTypes.push(...data.hierarchyTypes);
		state.classifierTypes.push(...data.classifierTypes);
		state.allDocStatuses.push(...data.allDocStatuses);
		state.docStatusSummary.push(...data.docStatusSummary);
		state.docStatusSummaryFiltered.push(...data.docStatusSummaryFiltered);
		state.docTypeSummary.push(...data.docTypeSummary);
		state.reviewGroups.push(...data.reviewGroups);
		state.reviewGroupsSummary.push(...data.reviewGroupsSummary);
		state.questionnaires.push(...data.questionnaires);
		state.peopleSummary.push(...data.peopleSummary);
		state.createExtraFields.push(...data.createExtraFields);
		state.templateLinks.push(...data.templateLinks);
		state.copyFromActions.push(...data.copyFromActions);
		state.listUpdated = new Date();
		state.fromTable = data.fromTable;
		state.docListColumns = data.docListColumns;

		state.loading = false;
	},
	addCopy(state, item) {
		if (state.useIndex) {
			setRefreshTimeout(state);
			return;
		}

		state.docsList.push(item);
		state.listUpdated = new Date();
	},
	update(state, item) {
		let index = state.docsList.findIndex((d) => d.doc_id === item.doc_id);
		if (index >= 0) {
			if (!item.overall_score || !item.overall_score_color) {
				item.overall_score = state.docsList[index].overall_score;
				item.overall_score_color = state.docsList[index].overall_score_color;
			}
			state.docsList.splice(index, 1, item);
		} else {
			state.docsList.push(item);
		}
		state.listUpdated = new Date();
	},
	remove(state, docId) {
		if (state.useIndex) {
			setRefreshTimeout(state);
			return;
		}

		let index = state.docsList.findIndex((d) => d.doc_id === docId);
		if (index >= 0) {
			state.docsList.splice(index, 1);
		}
		state.listUpdated = new Date();
	},
	updateScore(state, data) {
		if (!state.fromTable)
			return;

		if (state.useIndex) {
			setRefreshTimeout(state);
			return;
		}

		let doc = state.docsList.find((d) => d.doc_id === data.doc_id);
		if (doc && doc.overall_score !== data.overall_score && (doc.language_code === "" || doc.language_code === "en")) {
			doc.overall_score = data.overall_score;
			doc.overall_score_color = data.overall_score_color;
			state.listUpdated = new Date();
		}
	},
	updateCommentCount(state, data) {
		if (state.useIndex) {
			setRefreshTimeout(state);
			return;
		}
		
		let doc = state.docsList.find((d) => d.doc_id === data.doc_id);
		if (doc) {
			if (data.action === 'remove')
				doc.comment_count--;
			else
				doc.comment_count++;
			state.listUpdated = new Date();
		}
	},
	updateDocClassifiers(state, data) {
		if (state.useIndex) {
			setRefreshTimeout(state);
			return;
		}

		if (data && data.docIds && data.docIds.length && data.classifier) {
			data.docIds.forEach(doc_id => {
				const doc = state.docsList.find(d => d.doc_id === doc_id);
				if (doc) {
					const currentClassifier = doc.classifiers.find(c => c.ct_id === data.classifier.ct_id);
					doc[`classifier_${data.classifier.ct_id}`] = data.classifier.value;
					if (currentClassifier)
						currentClassifier.cv_id = data.classifier.cv_id;
					else
						doc.classifiers.push(data.classifier);
				}
			})
		}
	}
};

function prepareRow(row, lvCols, userName, hierarchyLookup, classifierLookup, templateLinks, copyFromActions, docListColumns, reviewGroups) {
	let item = JSON.parse(JSON.stringify(row)),
		match = "";
	delete item.parttypes;
	lvCols.forEach((c) => {
		item[c] = "";
	});

	item.hierarchies = item.hierarchies || [];
	if (hierarchyLookup && item.hierarchyIds && item.hierarchyIds.length) {
		item.hierarchies = item.hierarchyIds.map(id => {
			return hierarchyLookup['h' + id] || { hr_id: id };
		})
	}
	item.classifiers = item.classifiers || [];
	if (classifierLookup && item.classifierIds && item.classifierIds.length) {
		item.classifiers = item.classifierIds.map(id => {
			return classifierLookup['c' + id] || { cv_id: id };
		})
	}

	item.hierarchies.forEach((h) => {
		item[`listview${h.position}`] = h.hierarchy_text;
		[1, 2, 3, 4, 5, 6]
			.filter(l => h[`hierarchy${l}`])
			.forEach(l => item[`hry${h.position}_${l}`] = h[`hierarchy${l}`])
	});
	if (item.classifiers)
		item.classifiers.forEach(c => {
			item[`classifier_${c.ct_id}`] = c.value;
		});

	if (item.language_code !== "" && item.language_code !== "en")
		item.overall_score = null;

	if (item.lifecycle_id) {
		item.canCopyTo = copyFromActions
			.filter(a => a.lifecycle_id === item.lifecycle_id)
			.map(a => {
				return {
					tmpl_id: a.tmpl_id_copy_destination,
					tmpl_name: a.tmpl_copy_destination
				}
			});
	} else {
		item.canCopyTo = [
			{ tmpl_id: item.tmpl_id, tmpl_name: item.doc_type },
			...templateLinks
				.filter(l => l.tmpl_id_master === item.tmpl_id)
				.map(l => {
					return {
						tmpl_id: l.tmpl_id_linked,
						tmpl_name: l.tmpl_linked
					}
				})
		];
	}

	const doc_review_groups = [];
	if (item.review_group_ids) {
		item.review_group_ids.split(',').forEach(id => {
			const rg = reviewGroups.find(rg => rg.review_group_id == id);
			if (rg) {
				doc_review_groups.push(rg.name);
			}
		});
	} else {
		item.review_groups?.split(',').forEach(v => {
			if (v) {
				doc_review_groups.push(v);
			}
		});
	}
	item.review_groups_list = doc_review_groups;
	item.review_groups = doc_review_groups.join('; ');

	docListColumns.filter(c => c.searchWeighting).forEach(f => {
		match += item[f.value] ? ` ${item[f.value]}` : "";
	})

	item.match = match
		.toLowerCase()
		.split(" ")
		.filter((x) => x.length)
		.reduce((p, c) => p + c + "|", "|");
	const owners = [item.recruiter?.toLowerCase(), item.created_by?.toLowerCase(), ...(item.lifecyle_role_creator || "").toLowerCase().split(',')].filter(u => u);
	item.myJob = owners.includes(userName.toLowerCase());
	return item;
}

let docsRefreshTimeout;

export const actions = {
	init({ commit, getters, rootState }, loginState) {
		if (!getters.isInitialised) {
			commit("initialised");
			actions.setup({ commit, getters, rootState }, loginState?.user?.name);
		}
	},
	unload({ commit, getters }) {
		if (getters.isInitialised) {
			if (docsRefreshTimeout)
				clearTimeout(docsRefreshTimeout);

			commit("unload");
		}
	},
	refresh({ commit, getters, rootState }) {
		if (!getters.isInitialised || getters.loading) {
			return;
		}

		if (docsRefreshTimeout)
			clearTimeout(docsRefreshTimeout);

		actions.setup({ commit, getters, rootState }, getters.userName);
	},
	setup({ commit, getters, rootState }, userName) {
		if (!getters.isInitialised) return;

		axios
			.get("document/initdocslist/")
			.then(async (resp) => {
				if (resp.data.Status === "OK") {
					const data = {};
					data.userName = userName;
					data.hierarchyTypes = resp.data.Data.hierarchyTypes;
					data.classifierTypes = resp.data.Data.classifierTypes || [];
					data.createExtraFields = resp.data.Data.createExtraFields || [];
					data.templateLinks = resp.data.Data.templateLinks || [];
					data.copyFromActions = resp.data.Data.copyFromActions || [];
					data.reviewGroups = resp.data.Data.reviewGroups || [];
					data.docStatusSummary = resp.data.Data.statuses.map(s => {
						s.count = 0;
						s.position = Number(s.position) || 99;
						return s;
					}) || [];
					data.docTypeSummary = [];
					data.fromTable = resp.data.Data.fromTable || false;
					data.useIndex = resp.data.Data.useIndex;

					//wait until hierarchies are loaded.
					while (rootState.hierarchies.loading || rootState.settings.loading)
						await new Promise(resolve => setTimeout(resolve, 50));

					let translateSetting = resp.data.Data.settings.find(
						(x) => x.setting === "UseTranslation"
					);
					if (translateSetting) {
						data.useTranslation =
							translateSetting.value == "true" ? true : false;
					} else {
						data.useTranslation = false;
					}

					data.docListColumns = rootState.settings.config.docListColumns;
					data.reviewGroupsSummary = [];
					data.questionnaires = resp.data.Data.questionnaires || [];
					const peopleSummary = data.docListColumns
						.filter(c => c.isPersonColumn)
						.map(c => {
							return {
								label: c.text,
								column: c.value,
								values: []
							}
						});

					let lvCols = resp.data.Data.hierarchyTypes.map(
						(ht) => "listview" + ht.position
					);

					data.docsList = resp.data.Data.documents?.map((x) => {
						let n = prepareRow(x, lvCols, userName, rootState.hierarchies.hierarchyLookup, rootState.hierarchies.docClassifierLookup, data.templateLinks, data.copyFromActions, data.docListColumns, data.reviewGroups);

						let ss = data.docStatusSummary.find(
							(s) => s.status === n.doc_status_text
						);
						if (!ss) {
							let position = 99;
							let show = true;
							if (n.doc_status_text.startsWith('Part ')) {
								const sourceStatus = data.docStatusSummary.find(
									(s) => s.status === n.doc_status_text.replace('Part ', '')
								);
								if (sourceStatus && sourceStatus.position) position = sourceStatus.position - 0.5;
								if (sourceStatus) show = sourceStatus.show;
							}
							ss = { status: n.doc_status_text, count: 0, show: show, position: position };
							data.docStatusSummary.push(ss);
						}
						ss.count++;

						let st = data.docTypeSummary.find((s) => s.docType === n.doc_type);
						if (!st) {
							const template = rootState.hierarchies.docTypes.find(t => t.tmpl_id === n.tmpl_id);
							st = {
								docType: template?.tt_name,
								tt_id: template?.tt_id,
								tmpl_id: template?.tmpl_id,
								count: 0,
								docAbbrev: template?.abbreviation,
								showInAllJobs: template?.showInAllJobs
							};
							data.docTypeSummary.push(st);
						}
						st.count++;

						n.review_groups_list.forEach(v => {
							if (v) {
								if (!data.reviewGroupsSummary.includes(v))
									data.reviewGroupsSummary.push(v);
							}
						});

						peopleSummary.forEach(r => {
							x[r.column]?.split(',').forEach(v => {
								const vlower = v.toLowerCase();
								if (v && !r.values.some(p => p.toLowerCase() === vlower))
									r.values.push(v);
							});
						});

						return n;
					}) || [];

					if (data.useIndex) {
						const distinctFieldValues = resp.data.Data.distinctFieldValues || [];
						data.docTypeSummary = rootState.hierarchies.docTypes
							.filter(dt => !distinctFieldValues.doc_type || distinctFieldValues.doc_type.length === 0 || distinctFieldValues.doc_type.includes(dt.tmpl_name))
							.map(dt => ({
								docType: dt?.tt_name,
								tt_id: dt?.tt_id,
								tmpl_id: dt?.tmpl_id,
								count: 0,
								docAbbrev: dt?.abbreviation,
								showInAllJobs: dt?.showInAllJobs
							}));

						distinctFieldValues.doc_status_text
							?.filter(ds => !data.docStatusSummary.some(s => s.status === ds.trim()))
							?.forEach(ds => {
								if ((ds.startsWith('Part '))) {
									const sourceStatus = data.docStatusSummary.find(s => s.status === ds.replace('Part ', ''));
									if (sourceStatus) {
										data.docStatusSummary.splice(data.docStatusSummary.indexOf(sourceStatus),
											0,
											{ status: ds, show: sourceStatus.show, position: sourceStatus.position ? sourceStatus.position - 0.5 : 99, colour: sourceStatus.colour });
									} else {
										data.docStatusSummary.push({ status: ds, show: true, position: 99, colour: 'gray' });
									}
								} else {
									data.docStatusSummary.push({ status: ds, show: true, position: 99, colour: 'gray' });
								}
							});
						data.allDocStatuses = data.docStatusSummary;
						data.docStatusSummaryFiltered = data.docStatusSummary.filter(ds => !distinctFieldValues.doc_status_text || distinctFieldValues.doc_status_text.length === 0 || distinctFieldValues.doc_status_text.includes(ds.status.trim()));
						peopleSummary.forEach(r => {
							r.values = (distinctFieldValues[r.column] || []);
						});
						data.reviewGroupsSummary = distinctFieldValues['review_groups'] || [];
					} else {
						data.allDocStatuses = data.docStatusSummary;
						data.docStatusSummaryFiltered = [...data.docStatusSummary];
						data.docTypeSummary = data.docTypeSummary.filter(s => s.count);
					}

					data.reviewGroupsSummary.sort((a, b) => a.localeCompare(b, undefined, {sensitivity: 'base'}));
					data.docTypeSummary.sort((a, b) => a.tt_id - b.tt_id);
					data.peopleSummary = peopleSummary.filter(s => s.values.length)
						.map(s => {
							return { column: s.column, label: s.label, values: s.values.sort() };
						});
					
					
					
					commit("replace", data);

					//refresh every 10 mins
					docsRefreshTimeout = setTimeout(() => actions.setup({ commit, getters, rootState }, userName), 10 * 60 * 1000);
				}
			})
			.catch((err) => {
				if (err.response && err.response.status === 401) {
					commit("unload");
				} else {
					//try again in 30 secs
					docsRefreshTimeout = setTimeout(() => actions.setup({ commit, getters, rootState }, userName), 30 * 1000);
				}
				console.log(err);
			});
	},
	addCopy({ commit, state, rootState }, data) {
		let lvCols = state.hierarchyTypes.map(
			(ht) => "listview" + ht.position
		);
		let item = prepareRow(data, lvCols, state.userName, rootState.hierarchies.hierarchyLookup, rootState.hierarchies.docClassifierLookup, state.templateLinks, state.copyFromActions, state.docListColumns, state.reviewGroups);
		commit("addCopy", item);
	},
	updateDoc({ commit, state, rootState }, doc) {
		let lvCols = state.hierarchyTypes.map(
			(ht) => "listview" + ht.position
		);
		let item = prepareRow(doc, lvCols, state.userName, rootState.hierarchies.hierarchyLookup, rootState.hierarchies.docClassifierLookup, state.templateLinks, state.copyFromActions, state.docListColumns, state.reviewGroups);
		commit("update", item);
	},
	removeDoc({ commit }, docId) {
		commit("remove", docId);
	},
	updateScore({ commit }, data) {
		commit("updateScore", data);
	},
	updateCommentCount({ commit }, data) {
		commit("updateCommentCount", data);
	},
	updateDocClassifiers({ commit }, data) {
		commit("updateDocClassifiers", data);
	},
	getDocumentSummary({ commit, getters }, doc_id) {
		if (commit && getters)
			return new Promise((resolve, reject) => {
				axios
					.post("document/getDocumentSummary/", { doc_id: doc_id })
					.then((resp) => { 
						resolve(resp);
					})
					.catch((err) => {
						reject(err);
					});
			});
	},
};

const doOpenSearch = async (text, docTypeIncluded) => {
	let docTypes = docTypeIncluded?.map((d) => {
		return d.docType;
	}) || [];

	let data = {
		text: text,
		doc_types: docTypes,
	};

	let processPromise = new Promise((resolve, reject) => {
		axios
		.post("document/openSearch/", data)
		.then((resp) => {
			resolve(resp.data.Data);
		})
		.catch((err) => {
			reject(err);
		});
	});
	return processPromise;
};

export const getters = {
	isInitialised: (state) => {
		return state.initialised;
	},
	docsList: (state) => {
		return state.docsList;
	},
	userName: (state) => {
		return state.userName;
	},
	filter: (state) => async (params) => {
		return (await getters.pagedFilter(state, getters)(params)).documents;
	},
	pagedFilter: (state, getters) => async (params) => {
		const resp = {
			documents: [],
			totalCount: 0,
		}
		if (state.useIndex) {
			let indexResp = new Promise((resolve, reject) => axios.post("document/filterDocs/", params)
				.then(resp => {
					if (resp.data.Status === "OK") {
						// state.docStatusSummaryFiltered = state.docStatusSummary
						// 	.filter(x => resp.data.Data.distinctStatuses.includes(x.status.trim()));

						resolve({
							documents: resp.data.Data.documents || [],
							totalCount: resp.data.Data.totalCount || 0,
						});
					} else {
						reject(resp);
					}
				})
				.catch((err) => {
					reject(err);
				})
			);
			return indexResp;
		} else {
			let docs = [...state.docsList];

			if (params.docIdsIncluded)
				docs = docs.filter((doc) => params.docIdsIncluded.includes(doc.doc_id));

			if (params.systemNumbersIncluded)
				docs = docs.filter((doc) => params.systemNumbersIncluded.includes(doc.system_number));

			if (params.docIdsExcluded && params.docIdsExcluded.length)
				docs = docs.filter((doc) => !params.docIdsExcluded.includes(doc.doc_id));

			if (params.lifecycleIdsIncluded && params.lifecycleIdsIncluded.length)
				docs = docs.filter((doc) => params.lifecycleIdsIncluded.includes(doc.lifecycle_id));

			if (params.myJobs)
				docs = docs.filter((doc) => doc.myJob);

			if (params.docTypeIncluded && state.docTypeSummary.length > params.docTypeIncluded.length) {
				docs = docs.filter((d) =>
					params.docTypeIncluded.find((s) => s.docType === d.doc_type)
				);
			}
			state.docStatusSummaryFiltered = state.docStatusSummary.filter(x => docs.some(d => d.doc_status_text === x.status));

			if (params.canCopyTo) {
				docs = docs.filter((d) => d.doc_type === params.canCopyTo || d.canCopyTo.some(c => c.tmpl_name === params.canCopyTo));
			}

			if (params.docCreatedDateRange && params.docCreatedDateRange.length) {
				const fromDate = moment(params.docCreatedDateRange[0]);
				const toDate = params.docCreatedDateRange.length > 1 ? moment(params.docCreatedDateRange[1]) : fromDate.clone();

				if (fromDate.isSameOrBefore(toDate)) {
					toDate.add(1, 'days');
					docs = docs.filter((d) => {
						const created = moment(d.created_date);
						return created.isAfter(fromDate) && created.isBefore(toDate);
					});
				} else {
					fromDate.add(1, 'days');
					docs = docs.filter((d) => {
						const created = moment(d.created_date);
						return created.isAfter(toDate) && created.isBefore(fromDate);
					});
				}
			}

			if (params.docStatusIncluded) { // && state.docStatusSummary.length > params.docStatusIncluded.length) { - fix weird bug where hidden statuses passed through
				docs = docs.filter((d) =>
					params.docStatusIncluded.find((s) => s.status === d.doc_status_text)
				);
			}

			if (params.docScoresIncluded && state.docScoreCategories.length > params.docScoresIncluded.length) {
				docs = docs.filter((d) =>
					d.overall_score && params.docScoresIncluded.find((s) => d.overall_score >= s.min && d.overall_score <= s.max)
				);
			}

			if (params.hierarchyFilters) {
				params.hierarchyFilters.forEach((hf) => {
					if (hf.selected.length) {
						docs = docs.filter((d) =>
							hf.selected.find((s) => {
								let ht = d.hierarchies.find((ht) => ht.ht_id === hf.ht_id);
								return ht && s.id.split("_")[0] == ht.hr_id; // && !s.children
							})
						);
					}
				});
			}

			if (params.classifierFilters) {
				params.classifierFilters.forEach((cf) => {
					const cvs = cf.selected.map(c => c.cv_id);
					if (cf.selected.length) {
						docs = docs.filter((d) =>
							d.classifiers.some(c => cvs.includes(c.cv_id)) || (cvs.includes(0) && !d.classifiers.some(c => c.ct_id === cf.ct_id))
						);
					}
				});
			}

			if (params.reviewGroupsIncluded && params.reviewGroupsIncluded.length) {
				docs = docs.filter(d => {
					if (!d.review_groups_list)
						return false;

					const docGroups = d.review_groups_list;
					return params.reviewGroupsIncluded.some(i => docGroups.includes(i));
				})
			}

			if (params.peopleIncluded && params.peopleIncluded.length) {
				params.peopleIncluded
					.filter(f => f.values && f.values.length)
					.forEach(f => {
						docs = docs.filter(d => {
							if (!d[f.column])
								return false;

							const docUsers = d[f.column].toLowerCase().split(',');
							return f.values.some(v => docUsers.includes(v.toLowerCase()));
						});
					});
			}

			if (params.languageFilter) {
				docs = docs.filter(d => d.language_code == params.languageFilter);
			}

			if (params.fieldFilters) {
				Object.keys(params.fieldFilters).forEach(field => {
					const val = params.fieldFilters[field];
					const col = state.docListColumns.find(c => c.value === field);
					if (val && col) {
						if (col.isDate && Array.isArray(val) && val.length) {
							const fromDate = moment(val[0]);
							const toDate = val.length > 1 ? moment(val[1]) : fromDate.clone();

							if (fromDate.isSameOrBefore(toDate)) {
								toDate.add(1, 'days');
								docs = docs.filter((d) => {
									const date = d[field] ? moment(d[field], "DD MMM YYYY") : null;
									return date && date.isAfter(fromDate) && date.isBefore(toDate);
								});
							} else {
								fromDate.add(1, 'days');
								docs = docs.filter((d) => {
									const date = d[field] ?  moment(d[field], "DD MMM YYYY") : null;
									return date && date.isAfter(toDate) && date.isBefore(fromDate);
								});
							}
						} else if (col.isPersonColumn && Array.isArray(val) && val.length) {
							docs = docs.filter(d => {
								if (!d[field])
									return false;

								const docUsers = d[field].toLowerCase().split(',');
								return val.some(v => docUsers.includes(v.toLowerCase()));
							});
						} else if (typeof val === 'string') {
							const valLower = val.toLowerCase();
							docs = docs.filter(d => (d[field] || "").toLowerCase().indexOf(valLower) >= 0);
						} else if (Array.isArray(val)) {
							docs = docs.filter(d => val.includes((d[field] || "")));
						}
					}
				});
			}

			if (params.filterText) {
				let search = params.filterText
					.split(" ")
					.filter((x) => x.length)
					.map((x) => x);

				const simpleSearchDocs = docs.filter((d) =>
					search.every((s) => d.match.indexOf(s, 0) >= 0)
				);
				if (simpleSearchDocs.length === 0 && params.useOpenSearch) {
					const openSearchResults = await doOpenSearch(params.filterText, params.docTypeIncluded);
					const openSearchMinScore = params.openSearchMinScore || 5.0;
					const resultsMap = openSearchResults
						?.filter((x) => x.score >= openSearchMinScore)
						?.reduce((m, r) => {
							m.set(Number(r.id), r);
							return m;
						}, new Map());
					docs = docs
						.filter(d => resultsMap && resultsMap.has(d.doc_id))
						.map(d => {
							return {
								...d,
								matchRating: resultsMap.get(d.doc_id).score
							}
						});
				} else {
					docs = simpleSearchDocs;
				
					const exactMatchRegexp = new RegExp(params.filterText, "gi");
					const searchCols = state.docListColumns.filter(c => c.searchWeighting);
					docs.forEach((d, di) => {
						const marked = { ...d, matchRating: 0 };
						search.forEach((s, si) => {
							const matchRegexp = new RegExp(s, "gi");
							searchCols.forEach(col => {
								//prioritise exact matches when searching multiple words
								if (si === 0 && search.length > 1 && exactMatchRegexp.test(d[col.value]))
									marked.matchRating += col.searchWeighting * search.length;

								if (matchRegexp.test(d[col.value]))
									marked.matchRating += col.searchWeighting;

								let val = marked[col.value] || "",
									posMs = val.indexOf("<mark>"),
									posMe = val.indexOf("</mark>"),
									posPrev = 0,
									output = "";
								if (!(typeof val === 'string' || val instanceof String)) val = "";
								while (posMs >= 0) {
									if (posMs - posPrev > 0) {
										output += val
											.substring(posPrev, posMs)
											.replace(
												new RegExp(s, "gi"),
												(match) => `<mark>${match}</mark>`
											);
									}
									output += val.substring(posMs, posMe + 7);
									posPrev = posMe + 7;
									posMs = val.indexOf("<mark>", posMe);
									posMe = val.indexOf("</mark>", posMs);
								}
								if (posPrev < val.length) {
									output += val
										.substring(posPrev, val.length)
										.replace(
											matchRegexp,
											(match) => `<mark>${match}</mark>`
										);
								}
								marked[col.value] = output;
							})
						});
						//marked.system_number += ` (${marked.matchRating})`;
						docs.splice(di, 1, marked);
					});
				}
				docs.sort((a, b) => a.matchRating < b.matchRating ? 1 : -1);
			}
			docs.forEach((doc, index) => (doc.index = index + 1));
			resp.totalCount = docs.length;
			resp.documents = docs;
			return Promise.resolve(resp);
		}
	},
	getCountsByAttr: (state, getters) => async (params) => {
		let counts = [];
		if (state.useIndex) {
			await axios.post("document/getDocCountsByAttr/", params)
				.then(resp => {
					counts = resp.data || [];
				})
				.catch(e => {
					console.log(e);
				});
		}
		return counts;
	},
	getDocIndexGroupBy: (state, getters) => async (params) => {
		let counts = [];
		if (state.useIndex) {
			await axios.post("document/getDocIndexGroupBy/", params)
				.then(resp => {
					counts = resp.data || [];
				})
				.catch(e => {
					console.log(e);
				});
		}
		return counts;
	},
	getUserViewActions: (state, getters) => async (docIds) => {
		let viewActions = [];
		
		if (docIds.length) {
			if (state.useIndex) {
				console.log("getting view actions from index");
				await axios.get(`document/getUserViewActions/${docIds.join(',')}`)
					.then(resp => {
						viewActions = resp.data;
					})
					.catch(e => {
						console.log(e);
					});
			} else {
				viewActions = state.docsList
					.filter(d => docIds.includes(d.doc_id))
					.map(d => ({
						doc_id: d.doc_id,
						viewAction: d.viewAction
					}))
			}
		}


		return viewActions;
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
